import React from "react";
import StoryWelcome from "./StoryWelcome";
import CharacterLibrarySkeleton from "./CharacterLibrarySkeleton";
import StoryComposer, { StoryComposerToolbar, STORY_COMPOSER_BUTTON_SIZE, storyComposerColumnSx } from "./StoryComposer";
import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useDashboardLeftRail } from "../../contexts/DashboardShellContext";
import {
  APP_BACKGROUND,
  APP_BORDER,
  APP_LEFT_DRAWER_EXPANDED_WIDTH,
  APP_TEXT_SECONDARY,
} from "../createShared";
import {
  getCreateStoryContentSx,
  getStoredCharacterDrawerOpen,
  characterRailSizingSx,
  useCollapsedCharacterRailWidth,
} from "../createStoryLayout";

const placeholderSx = {
  bgcolor: "rgba(255,255,255,0.09)",
  "&::after": {
    background:
      "linear-gradient(90deg, transparent, rgba(216,163,99,0.17), transparent)",
  },
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    "&::after": { animation: "none" },
  },
} as const;

/** Presentational only: Create already owns its header's left-rail offset. */
export default function CreateStoryLoadingShell({
  expanded,
}: {
  expanded: boolean;
}) {
  const width = expanded
    ? APP_LEFT_DRAWER_EXPANDED_WIDTH
    : "var(--character-rail-width)";
  return (
    <Box
      role="status"
      aria-label="Loading Create a Story"
      aria-busy="true"
      sx={{
        bgcolor: APP_BACKGROUND,
        ...characterRailSizingSx,
        width: "100%",
        boxSizing: "border-box",
        px: 0,
      }}
    >
      <Box
        component="aside"
        aria-label="Loading characters"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width,
          height: "100dvh",
          boxSizing: "border-box",
          zIndex: 1400,
          bgcolor: APP_BACKGROUND,
          borderRight: `1px solid ${APP_BORDER}`,
          display: "flex",
          flexDirection: "column",
          alignItems: expanded ? "stretch" : "center",
          pt: expanded ? 2 : 0,
          pb: { xs: 2, md: 2.5 },
          px: expanded ? { xs: 1.5, md: 2.5 } : 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: expanded ? "space-between" : "center",
            height: expanded ? undefined : "var(--character-rail-heading-height)",
            mb: expanded ? 1.8 : 0,
            flexShrink: 0,
          }}
        >
          {expanded && (
            <Box sx={{ minWidth: 0, textAlign: "left" }}>
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: 22,
                  lineHeight: 1.1,
                }}
              >
                Characters
              </Typography>
              <Typography
                sx={{ color: APP_TEXT_SECONDARY, fontSize: 13, mt: 0.6 }}
              >
                Click to add them to your story.
              </Typography>
            </Box>
          )}
          <Box
            aria-hidden="true"
            sx={{
              width: expanded ? 42 : "var(--character-rail-item-size)",
              height: expanded ? 42 : "var(--character-rail-item-size)",
              "& svg": { fontSize: expanded ? 24 : "var(--character-rail-icon-size)" },
              flexShrink: 0,
              borderRadius: "50%",
              border: `1px solid ${APP_BORDER}`,
              bgcolor: "rgba(255,255,255,0.04)",
              color: APP_TEXT_SECONDARY,
              display: "grid",
              placeItems: "center",
            }}
          >
            {expanded ? <ChevronLeft /> : <ChevronRight />}
          </Box>
        </Box>
        <CharacterLibrarySkeleton expanded={expanded} />
        <Skeleton
          aria-hidden="true"
          animation="wave"
          variant="rounded"
          height={expanded ? 44 : "var(--character-rail-item-size)"}
          width={expanded ? "100%" : "var(--character-rail-item-size)"}
          sx={{
            ...placeholderSx,
            flexShrink: 0,
            mt: 1.5,
            borderRadius: expanded ? "14px" : "50%",
          }}
        />
      </Box>
      <Box
        aria-hidden="true"
        data-create-loading-composer
        sx={{
          ...getCreateStoryContentSx(expanded),
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
        }}
      >
        <Box sx={{ ...storyComposerColumnSx, flex: 1, display: 'flex' }}>
          <StoryWelcome accent="#D8A363" />
        </Box>
        <StoryComposer
          sx={{
            ...storyComposerColumnSx,
            flexShrink: 0,
          }}
        >
          <Skeleton
            animation="wave"
            width="68%"
            height={24}
            sx={placeholderSx}
          />
          <Skeleton
            animation="wave"
            width="43%"
            height={24}
            sx={{ ...placeholderSx, mt: 0.5 }}
          />
          <StoryComposerToolbar sx={{ gridTemplateColumns: '1fr auto' }}>
            <Skeleton
              animation="wave"
              variant="rounded"
              width={STORY_COMPOSER_BUTTON_SIZE}
              height={STORY_COMPOSER_BUTTON_SIZE}
              sx={placeholderSx}
            />
            <Skeleton
              animation="wave"
              variant="rounded"
              width={STORY_COMPOSER_BUTTON_SIZE}
              height={STORY_COMPOSER_BUTTON_SIZE}
              sx={placeholderSx}
            />
          </StoryComposerToolbar>
        </StoryComposer>
      </Box>
    </Box>
  );
}

/** The lazy-route fallback must reserve the same rail before Create mounts. */
export function CreateStoryRouteLoading() {
  const compact = useMediaQuery("(max-width:1180px)");
  const collapsedWidth = useCollapsedCharacterRailWidth();
  const expanded = !compact && getStoredCharacterDrawerOpen();
  useDashboardLeftRail(
    expanded ? APP_LEFT_DRAWER_EXPANDED_WIDTH : collapsedWidth,
  );
  return <CreateStoryLoadingShell expanded={expanded} />;
}
